import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import WallTabs from './components/WallTabs';
import { useIsRestaurant } from '../../../hooks/useAccountType';
import { useGetVideoWalls } from '../../../queries/videoWall';
import {
  useGetWallGroups, useCreateWallGroup, useDeleteWallGroup,
  useStartGroup, useStopGroup, useRefreshGroup,
} from '../../../queries/wallGroups';
import GroupMembersDialog from './components/GroupMembersDialog';

/**
 * Wall Groups — Phase 4 of the restaurant-chain platform redesign. Group walls,
 * manage members, and start/stop/refresh them together (fan-out; no cross-wall sync).
 */

/* One group card. Whole card → open (primary). Start/Stop/Refresh stay inline
   (frequent operational actions); Schedule / Manage walls / Delete fold into a
   single ⋮ overflow so the card keeps a clear hierarchy (Option A, 4+ actions). */
function GroupCard({ g, t, navigate, start, stop, refresh, onMembers, onDelete }) {
  const menuRef = React.useRef(null);
  const open = () => navigate(`/apps/video-wall/groups/${g.id}`);
  const overflowItems = [
    { label: t('video_wall.schedule.manage'), icon: 'pi pi-calendar',
      command: () => navigate(`/apps/video-wall/schedule?group=${g.id}`) },
    { label: t('video_wall.groups.manage_members'), icon: 'pi pi-users', command: () => onMembers(g) },
    { separator: true },
    { label: t('video_wall.groups.delete'), icon: 'pi pi-trash', className: 'pp-menu-item-danger',
      command: () => onDelete(g) },
  ];
  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div className="pp-card-clickable h-100 p-3 d-flex flex-column"
        style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)', boxShadow: 'var(--pp-shadow-sm)' }}
        role="button" tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
          <h5 className="mb-0 text-truncate" style={{ color: 'var(--pp-text-primary)' }}>{g.name}</h5>
          <div className="d-flex gap-1 align-items-center" style={{ flexShrink: 0 }}>
            {g.rows ? (
              <span style={{ background: 'var(--pp-primary)', color: '#fff', fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>
                {g.rows}×{g.columns}
              </span>
            ) : (
              <span style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)', fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>
                {t('video_wall.groups.any_geometry')}
              </span>
            )}
            <span style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)', fontWeight: 600, padding: '2px 10px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap' }}>
              {t('video_wall.groups.walls_count', { n: g.walls_count ?? 0 })}
            </span>
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap mt-2" onClick={(e) => e.stopPropagation()}>
          <Button label={t('video_wall.groups.start')} icon="pi pi-play" size="small" severity="success"
            loading={start.isLoading} disabled={!g.walls_count} onClick={() => start.mutate(g.id)} />
          <Button label={t('video_wall.groups.stop')} icon="pi pi-stop-circle" size="small" severity="danger" outlined
            loading={stop.isLoading} disabled={!g.walls_count} onClick={() => stop.mutate(g.id)} />
          <Button label={t('video_wall.groups.refresh')} icon="pi pi-sync" size="small" outlined
            loading={refresh.isLoading} disabled={!g.walls_count} onClick={() => refresh.mutate(g.id)} />
        </div>
        <div className="pp-card-actions mt-auto pt-3">
          <Button label={t('video_wall.groups.open')} icon="pi pi-arrow-right" iconPos="right" size="small"
            onClick={(e) => { e.stopPropagation(); open(); }} />
          <div className="pp-card-actions-end">
            <Menu model={overflowItems} popup ref={menuRef} appendTo={document.body} />
            <Button icon="pi pi-ellipsis-v" rounded text size="small" aria-haspopup
              aria-label={t('video_wall.more')}
              onClick={(e) => { e.stopPropagation(); menuRef.current?.toggle(e); }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VideoWallGroups() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();

  const { data: groups = [], isLoading } = useGetWallGroups();
  const { data: walls = [] } = useGetVideoWalls();
  const create = useCreateWallGroup();
  const del = useDeleteWallGroup();
  const start = useStartGroup();
  const stop = useStopGroup();
  const refresh = useRefreshGroup();

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [membersFor, setMembersFor] = useState(null); // group being edited

  const createGroup = async () => {
    const n = name.trim(); if (!n) return;
    await create.mutateAsync({ name: n });
    setCreating(false); setName('');
  };

  const confirmDelete = (g) => confirmDialog({
    message: t('video_wall.groups.delete_confirm', { name: g.name }),
    header: t('video_wall.groups.delete_title'),
    icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
    acceptLabel: t('video_wall.groups.delete'), rejectLabel: t('video_wall.groups.cancel'),
    accept: () => del.mutate(g.id),
  });

  return (
    <Layout>
      <ConfirmDialog />
      <WallTabs />
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
            <i className="fas fa-object-group me-2" style={{ color: 'var(--pp-primary)' }} />
            {t('video_wall.groups.title')}
          </h2>
          <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.groups.subtitle')}</p>
        </div>
        <div className="d-flex gap-2">
          {!isRestaurant && <Button label={t('video_wall.groups.back_to_walls')} icon="pi pi-th-large" outlined onClick={() => navigate('/apps/video-wall/walls')} />}
          <Button label={t('video_wall.groups.create')} icon="pi pi-plus" onClick={() => { setCreating(true); setName(''); }} />
        </div>
      </div>

      {isLoading ? (
        <div className="row g-3">{[...Array(3)].map((_, i) => <div key={i} className="col-12 col-md-6 col-lg-4"><Skeleton height="9rem" /></div>)}</div>
      ) : groups.length === 0 ? (
        <div className="text-center p-5" style={{ background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
          <div style={{ fontSize: '3rem' }}>🗂️</div>
          <h4 className="mt-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.groups.empty_title')}</h4>
          <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.groups.empty_subtitle')}</p>
          <Button label={t('video_wall.groups.create')} icon="pi pi-plus" onClick={() => { setCreating(true); setName(''); }} />
        </div>
      ) : (
        <div className="row g-3">
          {groups.map((g) => (
            <GroupCard key={g.id} g={g} t={t} navigate={navigate}
              start={start} stop={stop} refresh={refresh}
              onMembers={setMembersFor} onDelete={confirmDelete} />
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog visible={creating} onHide={() => setCreating(false)} header={t('video_wall.groups.create_title')} style={{ width: 'min(94vw, 440px)' }}
        footer={<div className="d-flex justify-content-end gap-2"><Button label={t('video_wall.groups.cancel')} text onClick={() => setCreating(false)} /><Button label={t('video_wall.groups.save')} icon="pi pi-check" loading={create.isLoading} disabled={!name.trim()} onClick={createGroup} /></div>}>
        <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.groups.name')}</label>
        <InputText className="w-100" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('video_wall.groups.name_ph')} maxLength={150} autoFocus />
      </Dialog>

      {/* Members dialog */}
      <GroupMembersDialog group={membersFor} walls={walls} onHide={() => setMembersFor(null)} />
    </Layout>
  );
}
